/**
 * Single source of truth for which Herald catalog component types are
 * exportable from the per-component hover toolbar, and how.
 *
 * `Card` is a first-class PNG export surface for bounded Herald “section”
 * content (title, body, chart, footer). Other layout primitives (`Stack`,
 * `Grid`) stay excluded — inner leaves (`Metric`, `Callout`, charts,
 * …) carry export when not under a card. Form / inline primitives (`Button`,
 * `Link`, `Badge`, …) and `SuggestionChips` are also excluded.
 */

export type HeraldExportMode = "png" | "csv";

const HERALD_EXPORTABLE_PNG_COMPONENT_TYPES = new Set<string>([
  "Card",
  "Callout",
  "DataList",
  "Timeline",
  "Metric",
  "MetricGroup",
  "Quote",
  "Image",
  "AreaChart",
  "ComposedChart",
  "LineChart",
  "BarChart",
  "StackedBarChart",
  "HorizontalBar",
  "PieChart",
  "RadarChart",
  "RadialBarChart",
  "SparkChartRow",
]);

const HERALD_EXPORTABLE_CSV_COMPONENT_TYPES = new Set<string>(["Table"]);

/**
 * Resolves the toolbar export mode for a catalog component type, or `null`
 * when the component should not show a hover export button.
 */
export const getHeraldComponentExportMode = (
  componentType: string,
): HeraldExportMode | null => {
  if (HERALD_EXPORTABLE_PNG_COMPONENT_TYPES.has(componentType)) {
    return "png";
  }
  if (HERALD_EXPORTABLE_CSV_COMPONENT_TYPES.has(componentType)) {
    return "csv";
  }
  return null;
};
